import { useMemo } from "react";
import { useSelector } from "react-redux";
import { UserSlice } from "store";
import type { UserRole } from "models/userRole";
import { ROLE_NAME_SECTOR_ADMIN, ROLE_NAME_PLANT_USER } from "pages/Users/constants";

/**
 * Helper function to get roles grouped by sector for a specific role name.
 * Returns one role per sector (the first one found for each sector).
 * 
 * @param userRoles - Array of user roles
 * @param targetRoleName - The role name to filter by (e.g., "Sector Admin", "Plant User")
 * @returns Array of UserRole objects, one per sector, sorted alphabetically by sector name
 */
export function GetRolesBySector(
	userRoles: UserRole[],
	targetRoleName: string
): UserRole[] {
	if (!userRoles?.length) return [];
	
	const sectorMap = new Map<string, UserRole>();
	userRoles.forEach((role) => {
		if (!role?.roleName || !role.containmentSectorName) return;
		const roleName = role.roleName.trim().toLowerCase();
		if (roleName === targetRoleName.toLowerCase() && !sectorMap.has(role.containmentSectorName)) {
			sectorMap.set(role.containmentSectorName, role);
		}
	});
	
	return Array.from(sectorMap.values()).sort((a, b) => {
		const nameA = (a?.containmentSectorName || "").toLowerCase();
		const nameB = (b?.containmentSectorName || "").toLowerCase();
		return nameA.localeCompare(nameB);
	});
}

/**
 * Helper function to get all role types grouped by sector.
 * Returns both Sector Admin and Plant User roles, one per sector each.
 * 
 * @param userRoles - Array of user roles
 * @returns Object containing sectorAdminRoles and plantUserRoles arrays
 */
export function GetAllRolesBySector(userRoles: UserRole[]) {
	if (!userRoles?.length) {
		return { sectorAdminRoles: [], plantUserRoles: [] };
	}
	
	return {
		sectorAdminRoles: GetRolesBySector(userRoles, ROLE_NAME_SECTOR_ADMIN),
		plantUserRoles: GetRolesBySector(userRoles, ROLE_NAME_PLANT_USER),
	};
}

/**
 * Hook to get current user's roles grouped by sector.
 * 
 * Provides convenient access to grouped roles from the Redux store.
 * 
 * @returns Object containing:
 *   - sectorAdminRoles: Array of Sector Admin roles, one per sector
 *   - plantUserRoles: Array of Plant User roles, one per sector
 *   - allRoles: All user roles from the store
 */
export function UseUserRoles() {
	const userRoles = useSelector(
		UserSlice.selectors.selectUserRoles
	) as UserRole[];

	const { sectorAdminRoles, plantUserRoles } = useMemo(
		() => GetAllRolesBySector(userRoles || []),
		[userRoles]
	);

	return {
		sectorAdminRoles,
		plantUserRoles,
		allRoles: userRoles || [],
	};
}
